import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {PromoService} from '../../../_services/promo.service';
import {PromoValidateResult} from '../../../_models/promo';

export interface PromoRedeemDialogData {
    orderId: string;
    orderTotal: number;    // in cents
}

export interface PromoRedeemDialogResult {
    tokenId: string;
    promoId: string;
    discountType: 'PERCENT' | 'FIXED';
    discountValue: number;
    discountAmount: number;   // calculated discount in cents
    promoName: string;
}

@Component({
    selector: 'app-promo-redeem-popup',
    templateUrl: './promo-redeem-popup.html',
    styleUrls: ['./promo-redeem-popup.component.scss']
})
export class PromoRedeemPopupComponent implements OnInit {

    tokenInput = '';
    validating = false;
    redeeming = false;
    validResult: PromoValidateResult | null = null;
    error: string | null = null;
    step: 'input' | 'confirm' | 'success' = 'input';

    constructor(
        private dialogRef: MatDialogRef<PromoRedeemPopupComponent>,
        private promoService: PromoService,
        @Inject(MAT_DIALOG_DATA) public data: PromoRedeemDialogData
    ) {}

    ngOnInit() {}

    validate() {
        if (!this.tokenInput.trim()) return;
        this.validating = true;
        this.error = null;
        this.validResult = null;

        this.promoService.validate(this.tokenInput.trim()).subscribe(
            result => {
                this.validating = false;
                if (result.valid) {
                    this.validResult = result;
                    this.step = 'confirm';
                } else {
                    this.error = result.message || 'Invalid promo code';
                }
            },
            () => { this.validating = false; this.error = 'Failed to validate code. Please try again.'; }
        );
    }

    get discountDisplay(): string {
        if (!this.validResult) return '';
        if (this.validResult.discountType === 'PERCENT') return `${this.validResult.discountValue}% off`;
        return `$${(this.validResult.discountValue / 100).toFixed(2)} off`;
    }

    get calculatedDiscount(): number {
        if (!this.validResult) return 0;
        if (this.validResult.discountType === 'PERCENT') {
            return Math.round(this.data.orderTotal * (this.validResult.discountValue / 100));
        }
        return Math.min(this.validResult.discountValue, this.data.orderTotal);
    }

    applyPromo() {
        if (!this.validResult) return;
        this.redeeming = true;

        this.promoService.redeem(this.validResult.tokenId || null, this.data.orderId, this.calculatedDiscount, this.validResult.promoId).subscribe(
            result => {
                this.redeeming = false;
                if (result.success) {
                    this.step = 'success';
                    setTimeout(() => {
                        this.dialogRef.close({
                            tokenId: this.validResult.tokenId || null,
                            promoId: this.validResult.promoId,
                            discountType: this.validResult.discountType,
                            discountValue: this.validResult.discountValue,
                            discountAmount: this.calculatedDiscount,
                            promoName: this.validResult.promoName
                        } as PromoRedeemDialogResult);
                    }, 1200);
                } else {
                    this.error = result.error === 'ALREADY_REDEEMED'
                        ? 'This promo has already been used'
                        : 'Failed to apply promo. Please try again.';
                    this.step = 'input';
                }
            },
            () => { this.redeeming = false; this.error = 'Failed to apply promo. Please try again.'; this.step = 'input'; }
        );
    }

    back() {
        this.step = 'input';
        this.validResult = null;
        this.error = null;
    }

    close() {
        this.dialogRef.close(null);
    }

    onTokenKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') this.validate();
    }
}
